import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { X } from "lucide-react";
export default function FooterSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [isAboutUsModalOpen, setIsAboutUsModalOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  const navLinks = ["About us", "Terms of Use", "Privacy Policy", "Contact Us"];
  return <section ref={sectionRef} className="dark">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');`}</style>

      <footer className="bg-background text-foreground">
        <div className={`max-w-6xl mx-auto px-6 pt-14 pb-10 transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0 mb-10">

            <div className="md:col-span-3 flex flex-col gap-6 pr-0 md:pr-8 mb-10 md:mb-0">
              <div className="text-xs font-medium tracking-[0.2em] uppercase" style={{
              color: "hsl(var(--muted-foreground))"
            }}>
                Information
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link, i) => {
                const isAboutUs = link === "About us";
                const isTerms = link === "Terms of Use";
                const isPrivacy = link === "Privacy Policy";
                const isContact = i === 3;
                return <Link key={link} data-index={i} to={isContact ? "mailto:info@verifiedandranked.com" : "#"} onClick={e => {
                  if (isAboutUs) {
                    e.preventDefault();
                    setIsAboutUsModalOpen(true);
                  } else if (isTerms) {
                    e.preventDefault();
                    setIsTermsOpen(true);
                  } else if (isPrivacy) {
                    e.preventDefault();
                    setIsPrivacyOpen(true);
                  }
                }} className="group flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 cursor-pointer">
                    <span className="inline-block w-4 h-px bg-muted-foreground group-hover:w-7 group-hover:bg-foreground transition-all duration-300" />
                    {link}
                  </Link>;
              })}
              </nav>
            </div>

            <div className="md:col-span-1 hidden md:flex justify-center">
              <div className="w-px h-full bg-border opacity-40" />
            </div>

            <Separator className="md:hidden mb-10 opacity-20" />

            <div className="md:col-span-5 flex flex-col gap-4 px-0 md:px-8 mb-10 md:mb-0">
              <div className="text-xs font-medium tracking-[0.2em] uppercase" style={{
              color: "hsl(var(--muted-foreground))"
            }}>
                Disclaimer
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This site is a free, independent resource. We may earn commissions through affiliate links (like Amazon Associates) when you buy through our site, which helps keep us running. This does not influence our rankings or editorial opinions.
              </p>
            </div>

            <div className="md:col-span-1 hidden md:flex justify-center">
              <div className="w-px h-full bg-border opacity-40" />
            </div>

            <Separator className="md:hidden mb-10 opacity-20" />

            <div className="md:col-span-2 flex flex-col items-start md:items-center gap-4 pl-0 md:pl-8">
              <div className="text-xs font-medium tracking-[0.2em] uppercase" style={{
              color: "hsl(var(--muted-foreground))"
            }}>
                Verified By
              </div>
              <div className="flex flex-col items-start md:items-center gap-3">
                <div className="relative group">
                  <div className="absolute inset-0 rounded-lg blur-md bg-primary opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                  
                </div>
                <Badge variant="outline" className="text-[10px] tracking-widest uppercase border-border text-muted-foreground px-2 py-0.5">
                  Consumer Reviews
                </Badge>
              </div>
            </div>
          </div>

          <Separator className="opacity-20 mb-8" />

          <div className="flex flex-col md:flex-row gap-6 md:gap-0 items-start md:items-start">
            <div className="flex-shrink-0 mr-0 md:mr-8">
              <div className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{
              color: "hsl(var(--muted-foreground))"
            }}>
                Written By
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground border border-border">ER</div>
                <div>
                  <div className="text-sm font-medium text-foreground leading-tight">Emma Richardson</div>
                  <div className="text-[10px] text-muted-foreground tracking-wide">
                    Dental Health &amp; Consumer Products Editor
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 border-l-0 md:border-l border-border pl-0 md:pl-8 border-opacity-30">
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">Emma has covered oral health and consumer dental products for 14 years across print and digital publications. She holds a degree in biomedical science from the University of Michigan and spent her first five years in clinical dental research before transitioning to product journalism. She has personally tested over 200 whitening treatments and oral care devices, and her work has appeared in the Journal of Consumer Health and Prevention Magazine.</p>
            </div>
          </div>
        </div>
      </footer>

      {isAboutUsModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setIsAboutUsModalOpen(false)}>
          <div className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-lg p-8 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <button onClick={() => setIsAboutUsModalOpen(false)} className="absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-900 transition-colors cursor-pointer" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
            <div className="text-slate-900">
              <h2 className="text-2xl font-bold mb-6">About Us</h2>
              <div className="space-y-6 text-slate-600 text-sm leading-relaxed">
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Mission</h3>
                  <p>At Tested & Ranked, our mission is to provide consumers with honest, data-driven insights into dental health products. We believe everyone deserves a confident smile, and we're here to help you find the most effective tools to achieve it through rigorous testing and transparent reporting.</p>
                </section>
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">How We Work</h3>
                  <p>Our process begins with extensive market research to identify the most popular and promising products. We then conduct real-world 30-day trials, documenting results, ease of use, and any side effects. Our rankings are based on objective criteria including efficacy, value, and ingredient safety.</p>
                </section>
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">What We Value</h3>
                  <p>Integrity, transparency, and scientific accuracy are at the core of everything we do. We value the trust our readers place in us and strive to maintain it by providing unbiased reviews that prioritize consumer health over corporate interests.</p>
                </section>
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">How We Make Money</h3>
                  <p>Tested & Ranked is supported by our readers. We may earn a small commission when you purchase products through our affiliate links. This comes at no extra cost to you and allows us to continue our independent testing without relying on brand sponsorships or paid placements.</p>
                </section>
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Contact Us</h3>
                  <p>Have questions or feedback? We'd love to hear from you. Reach out to our editorial team at <Link to="mailto:info@verifiedandranked.com" className="text-primary hover:underline">info@verifiedandranked.com</Link> for inquiries regarding our testing methodology or product recommendations.</p>
                </section>
              </div>
            </div>
          </div>
        </div>}

      {isTermsOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setIsTermsOpen(false)}>
          <div className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-lg p-8 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <button onClick={() => setIsTermsOpen(false)} className="absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-900 transition-colors cursor-pointer" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
            <div className="text-slate-900">
              <h2 className="text-2xl font-bold mb-6">Terms of Use</h2>
              <div className="space-y-6 text-slate-600 text-sm leading-relaxed">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Last Updated: January 1, 2024</p>
                
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">1. Acceptance of Terms</h3>
                  <p>By accessing and using Tested & Ranked, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">2. Use of the Site</h3>
                  <p>The content on this site is for your general information and use only. It is subject to change without notice. You agree to use the site only for lawful purposes and in a way that does not infringe the rights of, restrict or inhibit anyone else's use and enjoyment of the site.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">3. Intellectual Property</h3>
                  <p>This website contains material which is owned by or licensed to us. This material includes, but is not limited to, the design, layout, look, appearance, and graphics. Reproduction is prohibited other than in accordance with the copyright notice, which forms part of these terms and conditions.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4. Disclaimer of Warranties</h3>
                  <p>The information provided on Tested & Ranked is for informational purposes only. While we strive for accuracy, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability with respect to the website or the information, products, or services contained on the website.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">5. Limitation of Liability</h3>
                  <p>In no event will we be liable for any loss or damage including without limitation, indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss of data or profits arising out of, or in connection with, the use of this website.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6. Affiliate Disclosure</h3>
                  <p>Tested & Ranked participates in various affiliate marketing programs, which means we may get paid commissions on editorially chosen products purchased through our links to retailer sites. This does not affect our editorial integrity or the independence of our reviews.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">7. User-Generated Content</h3>
                  <p>If you post comments or other content on the site, you grant us a non-exclusive, royalty-free, perpetual, and irrevocable right to use, reproduce, modify, adapt, publish, and display such content throughout the world in any media.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">8. External Links</h3>
                  <p>Through this website, you are able to link to other websites which are not under the control of Tested & Ranked. We have no control over the nature, content, and availability of those sites. The inclusion of any links does not necessarily imply a recommendation or endorse the views expressed within them.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">9. Privacy</h3>
                  <p>Your use of this website is also governed by our <Link to="#" onClick={e => {
                  e.preventDefault();
                  setIsTermsOpen(false);
                  setIsPrivacyOpen(true);
                }} className="text-primary hover:underline">Privacy Policy</Link>, which is incorporated into these terms by reference.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">10. Governing Law</h3>
                  <p>Your use of this website and any dispute arising out of such use of the website is subject to the laws of the jurisdiction in which our company is registered.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">11. Changes to Terms</h3>
                  <p>We reserve the right to change these terms from time to time at our sole discretion. Your continued use of the site will signify your acceptance of any adjustment to these terms.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Contact Information</h3>
                  <p>If you have any questions about these Terms of Use, please contact us at <Link to="mailto:info@verifiedandranked.com" className="text-primary hover:underline">info@verifiedandranked.com</Link>.</p>
                </section>
              </div>
            </div>
          </div>
        </div>}

      {isPrivacyOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setIsPrivacyOpen(false)}>
          <div className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-lg p-8 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <button onClick={() => setIsPrivacyOpen(false)} className="absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-900 transition-colors cursor-pointer" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
            <div className="text-slate-900">
              <h2 className="text-2xl font-bold mb-6">Privacy Policy</h2>
              <div className="space-y-6 text-slate-600 text-sm leading-relaxed">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Last Updated: January 1, 2026</p>
                
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">1. Introduction</h3>
                  <p>Welcome to Tested & Ranked. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">2. Information We Collect</h3>
                  <p>We may collect personal information that you voluntarily provide to us, such as your name and email address when you subscribe to our newsletter or contact us. We also automatically collect certain technical data, including your IP address, browser type, and usage patterns through cookies.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">3. How We Use Your Information</h3>
                  <p>We use the information we collect to provide and improve our services, respond to your inquiries, send newsletters if you've opted in, and analyze website traffic to enhance user experience.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4. Cookies and Tracking Technologies</h3>
                  <p>Our website uses cookies to distinguish you from other users and to track how you interact with our content. You can manage your cookie preferences through your browser settings, though disabling cookies may affect site functionality.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">5. Affiliate Disclosure and Third Parties</h3>
                  <p>We participate in affiliate marketing programs. When you click on an affiliate link, a cookie may be placed on your browser to track any resulting purchase so we can earn a commission. We do not share your personal data with these affiliates.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6. Data Security</h3>
                  <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">7. Data Retention</h3>
                  <p>We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">8. Your Legal Rights</h3>
                  <p>Depending on your location, you may have rights under data protection laws, including the right to access, correct, or erase your personal data, and the right to object to or restrict certain processing.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">9. Third-Party Links</h3>
                  <p>Our website may include links to third-party websites. Clicking on those links may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">10. Children's Privacy</h3>
                  <p>Our website is not intended for children under the age of 13. We do not knowingly collect personal data from children under 13. If we learn we have collected such data, we will take steps to delete it.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">11. Changes to This Policy</h3>
                  <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">12. Contact Us</h3>
                  <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us at <Link to="mailto:info@verifiedandranked.com" className="text-primary hover:underline">info@verifiedandranked.com</Link>.</p>
                </section>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}